import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, Eye, EyeOff, MessageSquare, Briefcase, FileEdit, FileText, DollarSign, SlidersHorizontal, Sparkles, TrendingUp, Wand2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { DEFAULT_CONSULTANT_SYSTEM_PROMPT, DEFAULT_CONSULTANT_SYSTEM_PROMPT_STANDARD, DEFAULT_CONSULTANT_FOLLOW_UP_PROMPT } from '../constants/consultantDefaultPrompt';
import AIPromptsManager from '../components/AIPromptsManager';
import DocumentsEditor from '../components/DocumentsEditor';
import AuthSettingsTab from './AuthSettingsTab';

interface Survey {
  id: number;
  title: string;
  description: string | null;
  is_active: boolean;
  is_onboarding: boolean;
}

interface OAuthSettings {
  yandex: {
    enabled: boolean;
    clientId: string;
    clientSecret: string;
    callbackUrl: string;
  };
  telegram: {
    enabled: boolean;
    botToken: string;
    botUsername: string;
    webhookUrl: string;
    webappUrl: string;
  };
  google: {
    enabled: boolean;
    clientId: string;
    clientSecret: string;
    callbackUrl: string;
  };
  linkedin: {
    enabled: boolean;
    clientId: string;
    clientSecret: string;
    callbackUrl: string;
  };
}

const SettingsPage: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [activeOnboardingSurveyId, setActiveOnboardingSurveyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'onboarding' | 'oauth' | 'auth' | 'ai' | 'documents'>('onboarding');
  const [aiSubTab, setAiSubTab] = useState<'provider' | 'consultant' | 'vacancy_selection' | 'resume_creation' | 'motivation_letters' | 'salary_estimation' | 'vacancy_ai_modal' | 'career_report' | 'resume_apply_fix' | 'params'>('provider');
  const [aiUserTypeTab, setAiUserTypeTab] = useState<'premium' | 'free'>('premium'); // Табы Premium/Free внутри AI настроек
  
  // OAuth settings
  const [oauthSettings, setOAuthSettings] = useState<OAuthSettings | null>(null);
  const [oauthLoading, setOAuthLoading] = useState(false);
  const [oauthSaving, setOAuthSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState<{[key: string]: boolean}>({});
  
  // AI settings
  interface AISettings {
    provider: 'openai' | 'timeweb' | 'deepseek';
    model: string;
    openai?: {
      apiKey?: string;
      model?: string;
    };
    timeweb?: {
      apiKey?: string;
      baseUrl?: string;
      model?: string;
      accessId?: string;
    };
    deepseek?: {
      apiKey?: string;
      model?: string;
    };
    proxy?: {
      http?: string;
      https?: string;
      socks?: string;
    };
    systemPrompt?: string;
    welcomeMessage?: string;
    freeUserPrompt?: string;
    consultantFollowUpPrompt?: string;
    consultantSystemPromptStandard?: string;
    staticMessageFree?: string;
    staticMessageStandard?: string;
    staticMessagePro?: string;
    temperature?: number;
    topP?: number;
    maxCompletionTokens?: number;
    presencePenalty?: number;
    frequencyPenalty?: number;
    stopSequences?: string[];
    responseFormat?: 'text' | 'json_object' | 'json_schema';
    logprobs?: boolean;
    topLogprobs?: number;
  }
  
  const [aiSettings, setAISettings] = useState<AISettings | null>(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiSaving, setAISaving] = useState(false);
  const [availableModels, setAvailableModels] = useState<{[key: string]: Array<{id: string; name: string; description: string}>}>({});
  
  // AI Prompts management

  useEffect(() => {
    loadSurveys();
    loadOAuthSettings();
    loadAISettings();
  }, []);
  
  // Загружаем доступные модели при смене провайдера
  useEffect(() => {
    if (aiSettings?.provider) {
      loadAvailableModels(aiSettings.provider);
    }
  }, [aiSettings?.provider]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      // Попробуем загрузить опросы, если endpoint существует
      try {
        const response = await api.get('/surveys');
        if (response.data.success) {
          const surveysList = response.data.surveys || [];
          setSurveys(surveysList);
          
          // Находим активный опрос для онбординга
          const activeSurvey = surveysList.find((s: Survey) => s.is_onboarding && s.is_active);
          if (activeSurvey) {
            setActiveOnboardingSurveyId(activeSurvey.id);
          }
        }
      } catch (error: any) {
        // Если endpoint не существует, просто не загружаем опросы
        console.warn('Surveys endpoint not available:', error.message);
        setSurveys([]);
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки опросов');
    } finally {
      setLoading(false);
    }
  };

  const loadOAuthSettings = async () => {
    try {
      setOAuthLoading(true);
      const response = await api.get('/admin/settings/oauth');
      if (response.data.success && response.data.settings) {
        // Преобразуем ответ от API в формат для формы
        setOAuthSettings({
          yandex: {
            enabled: response.data.settings.yandex?.enabled || false,
            clientId: response.data.settings.yandex?.clientId || '',
            clientSecret: '', // Секреты не возвращаются из API
            callbackUrl: response.data.settings.yandex?.callbackUrl || ''
          },
          telegram: {
            enabled: response.data.settings.telegram?.enabled || false,
            botToken: '', // Токен не возвращается из API
            botUsername: response.data.settings.telegram?.botUsername || '',
            webhookUrl: response.data.settings.telegram?.webhookUrl || '',
            webappUrl: response.data.settings.telegram?.webappUrl || ''
          },
          google: {
            enabled: response.data.settings.google?.enabled || false,
            clientId: response.data.settings.google?.clientId || '',
            clientSecret: '', // Секреты не возвращаются из API
            callbackUrl: response.data.settings.google?.callbackUrl || ''
          },
          linkedin: {
            enabled: response.data.settings.linkedin?.enabled || false,
            clientId: response.data.settings.linkedin?.clientId || '',
            clientSecret: '', // Секреты не возвращаются из API
            callbackUrl: response.data.settings.linkedin?.callbackUrl || ''
          }
        });
      }
    } catch (error: any) {
      console.error('Ошибка загрузки OAuth настроек:', error);
      toast.error('Ошибка загрузки OAuth настроек');
    } finally {
      setOAuthLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (activeOnboardingSurveyId) {
        // Сначала деактивируем все опросы для онбординга
        const onboardingSurveys = surveys.filter(s => s.is_onboarding && s.id !== activeOnboardingSurveyId);
        for (const survey of onboardingSurveys) {
          try {
            await api.put(`/surveys/${survey.id}`, {
              ...survey,
              is_onboarding: false
            });
          } catch (error) {
            console.error('Ошибка обновления опроса:', error);
          }
        }
        
        // Активируем выбранный опрос
        try {
          const survey = surveys.find(s => s.id === activeOnboardingSurveyId);
          if (survey) {
            await api.put(`/surveys/${activeOnboardingSurveyId}`, {
              ...survey,
              is_onboarding: true
            });
          }
          toast.success('Настройки сохранены');
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Ошибка сохранения настроек');
        }
      } else {
        // Если опрос не выбран, деактивируем все опросы для онбординга
        const onboardingSurveys = surveys.filter(s => s.is_onboarding);
        for (const survey of onboardingSurveys) {
          try {
            await api.put(`/surveys/${survey.id}`, {
              ...survey,
              is_onboarding: false
            });
          } catch (error) {
            console.error('Ошибка обновления опроса:', error);
          }
        }
        toast.success('Активный опрос для онбординга сброшен');
      }
      
      // Перезагружаем список опросов
      await loadSurveys();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleOAuthSave = async () => {
    if (!oauthSettings) {
      toast.error('OAuth настройки не загружены');
      return;
    }
    
    try {
      setOAuthSaving(true);
      const response = await api.put('/admin/settings/oauth', oauthSettings);
      if (response.data.success) {
        toast.success('OAuth настройки обновлены');
        await loadOAuthSettings();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения OAuth настроек');
    } finally {
      setOAuthSaving(false);
    }
  };

  const toggleSecretVisibility = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Функция для извлечения Access ID из Base URL
  const extractAccessId = (baseUrl: string | undefined): string | null => {
    if (!baseUrl) return null;
    const match = baseUrl.match(/agents\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  };

  const loadAISettings = async () => {
    try {
      setAILoading(true);
      const response = await api.get('/admin/settings/ai');
      if (response.data.success && response.data.settings) {
        const settings = response.data.settings;
        setAISettings({
          provider: settings.provider || 'openai',
          model: settings.model || 'gpt-4o',
          openai: {
            apiKey: settings.openai?.apiKey || settings.openai_api_key || '',
            model: settings.openai?.model || settings.openai_model || 'gpt-4o'
          },
          timeweb: {
            apiKey: settings.timeweb?.apiKey || settings.timeweb_api_key || '',
            baseUrl: settings.timeweb?.baseUrl || settings.timeweb_base_url || '',
            model: settings.timeweb?.model || settings.timeweb_model || 'gpt-5-mini'
          },
          deepseek: {
            apiKey: settings.deepseek?.apiKey || settings.deepseek_api_key || '',
            model: settings.deepseek?.model || settings.deepseek_model || 'deepseek-chat'
          },
          proxy: settings.proxy || {
            http: settings.http_proxy || '',
            https: settings.https_proxy || '',
            socks: settings.socks_proxy || ''
          },
          systemPrompt: settings.systemPrompt || '',
          welcomeMessage: settings.welcomeMessage || '',
          freeUserPrompt: settings.freeUserPrompt || '',
          consultantFollowUpPrompt: settings.consultantFollowUpPrompt || '',
          consultantSystemPromptStandard: settings.consultantSystemPromptStandard || '',
          staticMessageFree: settings.staticMessageFree || '',
          staticMessageStandard: settings.staticMessageStandard || '',
          staticMessagePro: settings.staticMessagePro || '',
          temperature: settings.temperature !== undefined ? settings.temperature : 0.7,
          topP: settings.topP !== undefined ? settings.topP : 1.0,
          maxCompletionTokens: settings.maxCompletionTokens || 2000,
          presencePenalty: settings.presencePenalty !== undefined ? settings.presencePenalty : 0.0,
          frequencyPenalty: settings.frequencyPenalty !== undefined ? settings.frequencyPenalty : 0.0,
          stopSequences: settings.stopSequences || [],
          responseFormat: settings.responseFormat || 'text',
          logprobs: settings.logprobs !== undefined ? settings.logprobs : false,
          topLogprobs: settings.topLogprobs || 0
        });
        
        // Загружаем модели для текущего провайдера
        await loadAvailableModels(settings.provider || 'openai');
      }
    } catch (error: any) {
      console.error('Ошибка загрузки AI настроек:', error);
      toast.error('Ошибка загрузки AI настроек');
    } finally {
      setAILoading(false);
    }
  };

  const loadAvailableModels = async (provider: 'openai' | 'timeweb' | 'deepseek') => {
    try {
      if (availableModels[provider]) {
        return; // Уже загружены
      }
      
      const response = await api.get(`/admin/settings/ai/models/${provider}`);
      if (response.data.success && response.data.models) {
        setAvailableModels(prev => ({
          ...prev,
          [provider]: response.data.models
        }));
      }
    } catch (error: any) {
      console.error('Ошибка загрузки списка моделей:', error);
    }
  };

  const saveAISettings = async () => {
    if (!aiSettings) return;

    try {
      setAISaving(true);
      
      // Формируем данные для отправки
      const settingsData: any = {
        provider: aiSettings.provider,
        model: aiSettings.model || (aiSettings.provider === 'timeweb' ? 'gpt-5-mini' : aiSettings.provider === 'deepseek' ? 'deepseek-chat' : 'gpt-4o')
      };

      // Добавляем настройки в зависимости от провайдера
      if (aiSettings.provider === 'openai') {
        settingsData.openaiApiKey = aiSettings.openai?.apiKey || '';
        settingsData.openaiModel = aiSettings.openai?.model || aiSettings.model || 'gpt-4o';
      } else if (aiSettings.provider === 'timeweb') {
        settingsData.timewebApiKey = aiSettings.timeweb?.apiKey || '';
        settingsData.timewebBaseUrl = aiSettings.timeweb?.baseUrl || '';
        settingsData.timewebModel = aiSettings.timeweb?.model || aiSettings.model || 'gpt-5-mini';
      } else if (aiSettings.provider === 'deepseek') {
        settingsData.deepseekApiKey = aiSettings.deepseek?.apiKey || '';
        settingsData.deepseekModel = aiSettings.deepseek?.model || aiSettings.model || 'deepseek-chat';
      }

      // Прокси настройки (для OpenAI и DeepSeek)
      if (aiSettings.proxy) {
        settingsData.httpProxy = aiSettings.proxy.http || '';
        settingsData.httpsProxy = aiSettings.proxy.https || '';
        settingsData.socksProxy = aiSettings.proxy.socks || '';
      }

      // AI Assistant настройки
      settingsData.systemPrompt = aiSettings.systemPrompt || '';
      settingsData.welcomeMessage = aiSettings.welcomeMessage || '';
      settingsData.freeUserPrompt = aiSettings.freeUserPrompt || '';
      settingsData.consultantFollowUpPrompt = aiSettings.consultantFollowUpPrompt || '';
      settingsData.consultantSystemPromptStandard = aiSettings.consultantSystemPromptStandard || '';
      settingsData.staticMessageFree = aiSettings.staticMessageFree || '';
      settingsData.staticMessageStandard = aiSettings.staticMessageStandard || '';
      settingsData.staticMessagePro = aiSettings.staticMessagePro || '';
      settingsData.temperature = aiSettings.temperature !== undefined ? aiSettings.temperature : 0.7;
      settingsData.topP = aiSettings.topP !== undefined ? aiSettings.topP : 1.0;
      settingsData.maxCompletionTokens = aiSettings.maxCompletionTokens || 2000;
      settingsData.presencePenalty = aiSettings.presencePenalty !== undefined ? aiSettings.presencePenalty : 0.0;
      settingsData.frequencyPenalty = aiSettings.frequencyPenalty !== undefined ? aiSettings.frequencyPenalty : 0.0;
      settingsData.stopSequences = aiSettings.stopSequences || [];
      settingsData.responseFormat = aiSettings.responseFormat || 'text';
      settingsData.logprobs = aiSettings.logprobs !== undefined ? aiSettings.logprobs : false;
      settingsData.topLogprobs = aiSettings.topLogprobs || 0;

      const response = await api.put('/admin/settings/ai', settingsData);
      
      if (response.data.success) {
        toast.success('AI настройки успешно обновлены');
        await loadAISettings(); // Перезагружаем для получения обновленных данных
      } else {
        toast.error(response.data.message || 'Ошибка обновления настроек');
      }
    } catch (error: any) {
      console.error('Ошибка сохранения AI настроек:', error);
      toast.error(error.response?.data?.message || 'Ошибка сохранения AI настроек');
    } finally {
      setAISaving(false);
    }
  };

  if (loading && activeTab === 'onboarding') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (aiLoading && activeTab === 'ai') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Настройки</h1>
        <p className="text-gray-600 mt-2">Управление настройками системы</p>
      </div>

      {/* Main Tabs Navigation - Horizontal at top */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <nav className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('onboarding')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'onboarding'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Онбординг
          </button>
          <button
            onClick={() => setActiveTab('oauth')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'oauth'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            OAuth и интеграции
          </button>
          <button
            onClick={() => setActiveTab('auth')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'auth'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Вход и регистрация
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'ai'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            AI настройки
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'documents'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Документы
          </button>
        </nav>
      </div>

      {/* Content Area */}
      <div className={activeTab === 'ai' ? 'flex gap-6' : ''}>

      {/* Onboarding Settings */}
      {activeTab === 'onboarding' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Настройки онбординга
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Выберите опрос, который будет показываться пользователям при первом входе в систему.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Активный опрос для онбординга
              </label>
              <select
                value={activeOnboardingSurveyId || ''}
                onChange={(e) => setActiveOnboardingSurveyId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Не выбран</option>
                {surveys
                  .filter(s => s.is_active)
                  .map((survey) => (
                    <option key={survey.id} value={survey.id}>
                      {survey.title} {survey.is_onboarding ? '(текущий)' : ''}
                    </option>
                  ))}
              </select>
              {surveys.filter(s => s.is_active).length === 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  Нет активных опросов. Создайте и активируйте опрос в разделе "Опросы".
                </p>
              )}
            </div>

            {activeOnboardingSurveyId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Выбранный опрос будет показываться всем новым пользователям
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Убедитесь, что опрос активен и содержит все необходимые вопросы.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-5 w-5" />
                <span>{saving ? 'Сохранение...' : 'Сохранить настройки'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Документы */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <DocumentsEditor />
        </div>
      )}

      {/* OAuth Settings */}
      {activeTab === 'oauth' && (
        <div className="space-y-6">
          {oauthLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : oauthSettings ? (
            <>
              {/* Yandex OAuth */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Yandex OAuth</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.yandex?.clientId || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        yandex: { ...(oauthSettings.yandex || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientId: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Yandex Client ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets.yandexSecret ? 'text' : 'password'}
                        value={oauthSettings.yandex?.clientSecret || ''}
                        onChange={(e) => setOAuthSettings({
                          ...oauthSettings,
                          yandex: { ...(oauthSettings.yandex || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientSecret: e.target.value }
                        })}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Yandex Client Secret"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSecretVisibility('yandexSecret')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets.yandexSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Callback URL
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.yandex?.callbackUrl || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        yandex: { ...(oauthSettings.yandex || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), callbackUrl: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://rabotamax.ru/auth/yandex/callback"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="yandex-enabled"
                      checked={oauthSettings.yandex?.enabled || false}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        yandex: { ...(oauthSettings.yandex || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), enabled: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="yandex-enabled" className="ml-2 block text-sm text-gray-700">
                      Включить Yandex OAuth
                    </label>
                  </div>
                </div>
              </div>

              {/* Telegram Bot */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Telegram Bot</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bot Token
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets.telegramToken ? 'text' : 'password'}
                        value={oauthSettings.telegram?.botToken || ''}
                        onChange={(e) => setOAuthSettings({
                          ...oauthSettings,
                          telegram: { ...(oauthSettings.telegram || { enabled: false, botToken: '', botUsername: '', webhookUrl: '', webappUrl: '' }), botToken: e.target.value }
                        })}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Telegram Bot Token"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSecretVisibility('telegramToken')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets.telegramToken ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bot Username
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.telegram?.botUsername || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        telegram: { ...(oauthSettings.telegram || { enabled: false, botToken: '', botUsername: '', webhookUrl: '', webappUrl: '' }), botUsername: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Rabota_max_bot"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.telegram?.webhookUrl || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        telegram: { ...(oauthSettings.telegram || { enabled: false, botToken: '', botUsername: '', webhookUrl: '', webappUrl: '' }), webhookUrl: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://rabotamax.ru/api/telegram/webhook"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Web App URL
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.telegram?.webappUrl || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        telegram: { ...(oauthSettings.telegram || { enabled: false, botToken: '', botUsername: '', webhookUrl: '', webappUrl: '' }), webappUrl: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://rabotamax.ru"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="telegram-enabled"
                      checked={oauthSettings.telegram?.enabled || false}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        telegram: { ...(oauthSettings.telegram || { enabled: false, botToken: '', botUsername: '', webhookUrl: '', webappUrl: '' }), enabled: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="telegram-enabled" className="ml-2 block text-sm text-gray-700">
                      Включить Telegram Bot
                    </label>
                  </div>
                </div>
              </div>

              {/* Google OAuth */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Google OAuth</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.google?.clientId || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        google: { ...(oauthSettings.google || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientId: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Google Client ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets.googleSecret ? 'text' : 'password'}
                        value={oauthSettings.google?.clientSecret || ''}
                        onChange={(e) => setOAuthSettings({
                          ...oauthSettings,
                          google: { ...(oauthSettings.google || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientSecret: e.target.value }
                        })}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Google Client Secret"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSecretVisibility('googleSecret')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets.googleSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Callback URL
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.google?.callbackUrl || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        google: { ...(oauthSettings.google || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), callbackUrl: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://rabotamax.ru/auth/google/callback"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="google-enabled"
                      checked={oauthSettings.google?.enabled || false}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        google: { ...(oauthSettings.google || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), enabled: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="google-enabled" className="ml-2 block text-sm text-gray-700">
                      Включить Google OAuth
                    </label>
                  </div>
                </div>
              </div>

              {/* LinkedIn OAuth */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn OAuth</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.linkedin?.clientId || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        linkedin: { ...(oauthSettings.linkedin || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientId: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="LinkedIn Client ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets.linkedinSecret ? 'text' : 'password'}
                        value={oauthSettings.linkedin?.clientSecret || ''}
                        onChange={(e) => setOAuthSettings({
                          ...oauthSettings,
                          linkedin: { ...(oauthSettings.linkedin || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), clientSecret: e.target.value }
                        })}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="LinkedIn Client Secret"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSecretVisibility('linkedinSecret')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSecrets.linkedinSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Callback URL
                    </label>
                    <input
                      type="text"
                      value={oauthSettings.linkedin?.callbackUrl || ''}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        linkedin: { ...(oauthSettings.linkedin || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), callbackUrl: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://rabotamax.ru/auth/linkedin/callback"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="linkedin-enabled"
                      checked={oauthSettings.linkedin?.enabled || false}
                      onChange={(e) => setOAuthSettings({
                        ...oauthSettings,
                        linkedin: { ...(oauthSettings.linkedin || { enabled: false, clientId: '', clientSecret: '', callbackUrl: '' }), enabled: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="linkedin-enabled" className="ml-2 block text-sm text-gray-700">
                      Включить LinkedIn OAuth
                    </label>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleOAuthSave}
                  disabled={oauthSaving}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="h-5 w-5" />
                  <span>{oauthSaving ? 'Сохранение...' : 'Сохранить OAuth настройки'}</span>
                </button>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-600">Не удалось загрузить OAuth настройки</p>
            </div>
          )}
        </div>
      )}

      {/* AI Settings */}
      {/* Вход и регистрация (149-ФЗ): провайдеры + политика email-доменов */}
      {activeTab === 'auth' && <AuthSettingsTab />}

      {activeTab === 'ai' && (
        <>
          {/* AI Sub-sections Sidebar - Left */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md">
              <nav className="flex flex-col p-2">
                <button
                  onClick={() => setAiSubTab('provider')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'provider'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>Провайдер</span>
                </button>
                <button
                  onClick={() => setAiSubTab('consultant')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'consultant'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>AI Консультант</span>
                </button>
                <button
                  onClick={() => setAiSubTab('vacancy_selection')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'vacancy_selection'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Briefcase className="h-4 w-4" />
                  <span>Выбор вакансий</span>
                </button>
                <button
                  onClick={() => setAiSubTab('resume_creation')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'resume_creation'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FileEdit className="h-4 w-4" />
                  <span>Создание резюме</span>
                </button>
                <button
                  onClick={() => setAiSubTab('motivation_letters')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'motivation_letters'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  <span>Мотивационные письма</span>
                </button>
                <button
                  onClick={() => setAiSubTab('salary_estimation')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'salary_estimation'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <DollarSign className="h-4 w-4" />
                  <span>Оценка зарплаты</span>
                </button>
                <button
                  onClick={() => setAiSubTab('vacancy_ai_modal')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'vacancy_ai_modal'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                  <span>ИИ-консультант на вакансии</span>
                </button>
                <button
                  onClick={() => setAiSubTab('career_report')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'career_report'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  <span>Карьерный отчёт</span>
                </button>
                <button
                  onClick={() => setAiSubTab('resume_apply_fix')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'resume_apply_fix'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Wand2 className="h-4 w-4" />
                  <span>Адаптировать резюме</span>
                </button>
                <button
                  onClick={() => setAiSubTab('params')}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left flex items-center space-x-2 ${
                    aiSubTab === 'params'
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span>Параметры запроса</span>
                </button>
              </nav>
            </div>
          </div>

          {/* AI Content Area - Right */}
          <div className="flex-1 space-y-6">

          {aiLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : aiSettings ? (
            <>
              {/* Provider Settings Sub-tab */}
              {aiSubTab === 'provider' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">AI Провайдер</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Провайдер
                    </label>
                    <select
                      value={aiSettings.provider}
                      onChange={(e) => {
                        const newProvider = e.target.value as 'openai' | 'timeweb' | 'deepseek';
                        setAISettings({
                          ...aiSettings,
                          provider: newProvider,
                          model: newProvider === 'timeweb' ? 'gpt-5-mini' : newProvider === 'deepseek' ? 'deepseek-chat' : 'gpt-4o'
                        });
                        loadAvailableModels(newProvider);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="timeweb">Timeweb Agent</option>
                      <option value="deepseek">DeepSeek</option>
                    </select>
                  </div>

                  {aiSettings.provider !== 'timeweb' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Модель
                      </label>
                      <select
                        value={aiSettings.model}
                        onChange={(e) => setAISettings({ ...aiSettings, model: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {availableModels[aiSettings.provider]?.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} - {model.description}
                          </option>
                        )) || (
                          <option value={aiSettings.model || 'gpt-4o'}>
                            {aiSettings.model || 'gpt-4o'}
                          </option>
                        )}
                      </select>
                      {availableModels[aiSettings.provider]?.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Загрузка списка моделей...
                        </p>
                      )}
                    </div>
                  )}
                  
                  {aiSettings.provider === 'timeweb' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Модель
                      </label>
                      <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
                        {aiSettings.timeweb?.model || aiSettings.model || 'gpt-5-mini'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Модель задается в конфигурации агента на стороне Timeweb. Это поле только для отображения.
                      </p>
                    </div>
                  )}
                  {aiSettings.provider === 'timeweb' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Примечание:</strong> Для Timeweb Agent модель задается в конфигурации агента на стороне Timeweb. 
                        Поле модели в запросе игнорируется. Текущая модель, настроенная для вашего агента, будет использоваться автоматически.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* OpenAI Settings */}
              {aiSubTab === 'provider' && aiSettings.provider === 'openai' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">OpenAI Настройки</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key
                      </label>
                      <input
                        type="password"
                        value={aiSettings.openai?.apiKey || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          openai: { ...aiSettings.openai, apiKey: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="sk-..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Модель OpenAI
                      </label>
                      <input
                        type="text"
                        value={aiSettings.openai?.model || aiSettings.model || 'gpt-4o'}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          openai: { ...aiSettings.openai, model: e.target.value },
                          model: e.target.value
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="gpt-4o"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Timeweb Agent Settings */}
              {aiSubTab === 'provider' && aiSettings.provider === 'timeweb' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Timeweb Agent Настройки</h2>
                  <div className="space-y-4">
                    {/* Токен (JWT) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        JWT Token
                      </label>
                      <div className="relative">
                        <input
                          type={showSecrets.timewebToken ? 'text' : 'password'}
                          value={aiSettings.timeweb?.apiKey || ''}
                          onChange={(e) => setAISettings({
                            ...aiSettings,
                            timeweb: { ...aiSettings.timeweb, apiKey: e.target.value }
                          })}
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVC..."
                        />
                        <button
                          type="button"
                          onClick={() => toggleSecretVisibility('timewebToken')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets.timewebToken ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Base URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Base URL
                      </label>
                      <input
                        type="text"
                        value={aiSettings.timeweb?.baseUrl || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          timeweb: { ...aiSettings.timeweb, baseUrl: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://agent.timeweb.cloud/api/v1/cloud-ai/agents/..."
                      />
                    </div>
                    
                    {/* Access ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agent Access ID
                      </label>
                      <input
                        type="text"
                        value={extractAccessId(aiSettings.timeweb?.baseUrl) || ''}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 cursor-not-allowed font-mono text-sm"
                        placeholder="Будет автоматически извлечен из Base URL"
                      />
                      {!extractAccessId(aiSettings.timeweb?.baseUrl) && aiSettings.timeweb?.baseUrl && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ Access ID не найден в Base URL. Убедитесь, что URL содержит путь к агенту (например: .../agents/535b1320-5ac7-493e-abe4-1b73591cf9e1/...)
                        </p>
                      )}
                      {!extractAccessId(aiSettings.timeweb?.baseUrl) && !aiSettings.timeweb?.baseUrl && (
                        <p className="text-xs text-gray-500 mt-1">
                          Access ID будет автоматически извлечен после ввода Base URL
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* DeepSeek Settings */}
              {aiSubTab === 'provider' && aiSettings.provider === 'deepseek' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">DeepSeek Настройки</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key
                      </label>
                      <input
                        type="password"
                        value={aiSettings.deepseek?.apiKey || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          deepseek: { ...aiSettings.deepseek, apiKey: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="sk-..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Модель
                      </label>
                      <input
                        type="text"
                        value={aiSettings.deepseek?.model || aiSettings.model || 'deepseek-chat'}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          deepseek: { ...aiSettings.deepseek, model: e.target.value },
                          model: e.target.value
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="deepseek-chat"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Proxy Settings (for OpenAI and DeepSeek) */}
              {aiSubTab === 'provider' && (aiSettings.provider === 'openai' || aiSettings.provider === 'deepseek') && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Прокси Настройки</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HTTP Proxy
                      </label>
                      <input
                        type="text"
                        value={aiSettings.proxy?.http || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          proxy: { ...aiSettings.proxy, http: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="http://proxy.example.com:8080"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HTTPS Proxy
                      </label>
                      <input
                        type="text"
                        value={aiSettings.proxy?.https || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          proxy: { ...aiSettings.proxy, https: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://proxy.example.com:8080"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SOCKS Proxy (для VPN)
                      </label>
                      <input
                        type="text"
                        value={aiSettings.proxy?.socks || ''}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          proxy: { ...aiSettings.proxy, socks: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="socks5://127.0.0.1:1080"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* AI Consultant Sub-tab */}
              {aiSubTab === 'consultant' && (
                <>
                  {/* Premium/Free Tabs */}
                  <div className="bg-white rounded-lg shadow-md mb-6">
                    <div className="border-b border-gray-200">
                      <nav className="flex -mb-px">
                        <button
                          onClick={() => setAiUserTypeTab('premium')}
                          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                            aiUserTypeTab === 'premium'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Стандарт / Про
                        </button>
                        <button
                          onClick={() => setAiUserTypeTab('free')}
                          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                            aiUserTypeTab === 'free'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Free
                        </button>
                      </nav>
                    </div>
                  </div>

                  {/* Стандарт / Про: анализ + продолжение диалога */}
                  {aiUserTypeTab === 'premium' && (
                    <>
                      {/* Системный промпт: первый ответ (анализ) */}
                      <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Системный промпт (первый ответ — анализ)</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Промпт для первого запроса: анализ резюме и вакансии
                            </label>
                            <textarea
                              value={aiSettings.systemPrompt || ''}
                              onChange={(e) => setAISettings({
                                ...aiSettings,
                                systemPrompt: e.target.value
                              })}
                              rows={15}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                              placeholder="Введите системный промпт для первого ответа (анализ)..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Используется один раз при первом запросе: анализ резюме и вакансии, рекомендации. В конце ответа — разный текст для тарифов Standard и Pro (указан в промпте).
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setAISettings({
                                ...aiSettings,
                                systemPrompt: DEFAULT_CONSULTANT_SYSTEM_PROMPT
                              })}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Восстановить по умолчанию (полный промпт консультанта)
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Системный промпт: продолжение диалога (только Про) */}
                      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Системный промпт (продолжение диалога)</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Промпт для последующих сообщений после анализа
                            </label>
                            <textarea
                              value={aiSettings.consultantFollowUpPrompt || ''}
                              onChange={(e) => setAISettings({
                                ...aiSettings,
                                consultantFollowUpPrompt: e.target.value
                              })}
                              rows={8}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                              placeholder="Введите промпт для режима диалога после анализа..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Используется для пользователей Про при последующих сообщениях после анализа. Ассистент отвечает на вопросы по контексту и истории, не повторяет полный анализ.
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setAISettings({
                                ...aiSettings,
                                consultantFollowUpPrompt: DEFAULT_CONSULTANT_FOLLOW_UP_PROMPT
                              })}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Восстановить по умолчанию
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Промпт для тарифа Standard */}
                      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Промпт для тарифа Standard</h2>
                        <p className="text-sm text-gray-500 mb-2">Системный промпт для пользователей тарифа Standard: тот же анализ и рекомендации, без инструкции по извлечению в JSON (экономия токенов). Если пусто — используется общий промпт выше.</p>
                        <textarea
                          value={aiSettings.consultantSystemPromptStandard || ''}
                          onChange={(e) => setAISettings({ ...aiSettings, consultantSystemPromptStandard: e.target.value })}
                          rows={8}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          placeholder="Оставьте пустым, чтобы использовать общий промпт..."
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            type="button"
                            onClick={() => setAISettings({
                              ...aiSettings,
                              consultantSystemPromptStandard: DEFAULT_CONSULTANT_SYSTEM_PROMPT_STANDARD
                            })}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Восстановить по умолчанию
                          </button>
                        </div>
                      </div>

                      {/* Статические сообщения после анализа */}
                      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Статические сообщения после анализа</h2>
                        <p className="text-sm text-gray-500 mb-4">Показываются пользователю после первого ответа консультанта (анализ резюме и вакансии). Выберите по тарифу.</p>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Free</label>
                            <textarea
                              value={aiSettings.staticMessageFree || ''}
                              onChange={(e) => setAISettings({ ...aiSettings, staticMessageFree: e.target.value })}
                              rows={2}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Сообщение для Free после анализа..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Standard (призыв к Про)</label>
                            <textarea
                              value={aiSettings.staticMessageStandard || ''}
                              onChange={(e) => setAISettings({ ...aiSettings, staticMessageStandard: e.target.value })}
                              rows={2}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="На следующем этапе ты сможешь применить рекомендации..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Про (продолжить консультацию)</label>
                            <textarea
                              value={aiSettings.staticMessagePro || ''}
                              onChange={(e) => setAISettings({ ...aiSettings, staticMessagePro: e.target.value })}
                              rows={2}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Далее ты можешь продолжить консультацию и добавить навыки, опыт..."
                            />
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setAISettings({
                                ...aiSettings,
                                staticMessageStandard: 'На следующем этапе ты сможешь применить все эти рекомендации и воспользоваться другими возможностями тарифа. Чтобы дополнить опыт, образование и навыки в чате и перейти к автозаполнению резюме — нажми кнопку ниже.',
                                staticMessagePro: 'Далее ты можешь продолжить со мной консультацию и добавление навыков, образования, курсов и т.д., чтобы улучшить рекомендации под вакансию — мы извлечём их на следующем этапе.'
                              })}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Подставить стандартные тексты
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Welcome Message Section */}
                      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Приветственное сообщение (Стандарт / Про)</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Приветственное сообщение для пользователей Стандарт и Про
                            </label>
                            <textarea
                              value={aiSettings.welcomeMessage || ''}
                              onChange={(e) => setAISettings({
                                ...aiSettings,
                                welcomeMessage: e.target.value
                              })}
                              rows={4}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Введите приветственное сообщение..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Показывается при первом запуске AI ассистента в CreateProjectFlow (тарифы Standard и Pro).
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Free Settings */}
                  {aiUserTypeTab === 'free' && (
                    <>
                      {/* System Prompt Section for Free */}
                      <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Системный промпт (Free)</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Системный промпт для Free пользователей
                            </label>
                            <textarea
                              value={aiSettings.freeUserPrompt || ''}
                              onChange={(e) => setAISettings({
                                ...aiSettings,
                                freeUserPrompt: e.target.value
                              })}
                              rows={15}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                              placeholder="Введите системный промпт для Free пользователей..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Системный промпт для Free пользователей. Ассистент должен получить 1 достижение от пользователя, затем предложить подписку Premium.
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                const defaultFreePrompt = `Ты - опытный руководитель продаж и эксперт по продажам с глубоким пониманием психологии покупателей. Ты знаешь, как общаться с разными психотипами людей и можешь закрыть любую продажу, независимо от возражений клиента.

Твоя задача:
1. Помочь пользователю сформулировать одно достижение для резюме
2. После получения достижения, предложить Premium подписку
3. Использовать техники продаж для закрытия сделки

Как работать:
- Сначала помоги сформулировать одно достижение
- После получения достижения, переходи к продаже Premium подписки
- Используй техники продаж: выявление потребностей, работа с возражениями, создание срочности
- Будь настойчивым, но не навязчивым
- Адаптируйся под психотип клиента

Стиль общения:
- Будь дружелюбным и профессиональным
- Отвечай на русском языке
- Пиши обычным текстом, без markdown`;
                                setAISettings({
                                  ...aiSettings,
                                  freeUserPrompt: defaultFreePrompt
                                });
                              }}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Восстановить по умолчанию
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Welcome Message Section for Free */}
                      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Приветственное сообщение (Free)</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Приветственное сообщение для Free пользователей
                            </label>
                            <textarea
                              value={aiSettings.freeUserPrompt ? aiSettings.freeUserPrompt.split('\n')[0] : ''}
                              onChange={(e) => {
                                // Обновляем только первую строку freeUserPrompt как приветственное сообщение
                                const currentPrompt = aiSettings.freeUserPrompt || '';
                                const lines = currentPrompt.split('\n');
                                lines[0] = e.target.value;
                                setAISettings({
                                  ...aiSettings,
                                  freeUserPrompt: lines.join('\n')
                                });
                              }}
                              rows={4}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Введите приветственное сообщение для Free пользователей..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Это сообщение показывается Free пользователям при первом запуске AI ассистента. Обычно содержит информацию о том, что можно получить одно достижение, затем будет предложена Premium подписка.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              
              {/* Vacancy Selection Sub-tab */}
              {aiSubTab === 'vacancy_selection' && (
                <AIPromptsManager
                  stage="vacancy_selection"
                  promptTypes={['parse_vacancy_text']}
                  stageName="Выбор вакансий"
                />
              )}
              
              {/* Resume Creation Sub-tab */}
              {aiSubTab === 'resume_creation' && (
                <AIPromptsManager
                  stage="resume_creation"
                  promptTypes={['extract_resume_data', 'improve_resume', 'match_vacancy']}
                  stageName="Создание резюме"
                />
              )}
              
              {/* Motivation Letters Sub-tab */}
              {aiSubTab === 'motivation_letters' && (
                <AIPromptsManager
                  stage="motivation_letters"
                  promptTypes={['generate_letter']}
                  stageName="Мотивационные письма"
                />
              )}
              
              {/* Salary Estimation Sub-tab */}
              {aiSubTab === 'salary_estimation' && (
                <AIPromptsManager
                  stage="salary_estimation"
                  promptTypes={['estimate_salary']}
                  stageName="Оценка зарплаты"
                />
              )}
              
              {/* Vacancy AI Modal Sub-tab (Советы + Сопроводительное письмо) */}
              {aiSubTab === 'vacancy_ai_modal' && (
                <AIPromptsManager
                  stage="vacancy_ai_modal"
                  promptTypes={['advise', 'cover_letter_template', 'cover_letter_auto_apply']}
                  stageName="ИИ-консультант на вакансии"
                />
              )}

              {/* Career Report Sub-tab */}
              {aiSubTab === 'career_report' && (
                <AIPromptsManager
                  stage="career_report"
                  promptTypes={['generate_report']}
                  stageName="Карьерный отчёт"
                />
              )}

              {/* Resume Apply-Fix Sub-tab */}
              {aiSubTab === 'resume_apply_fix' && (
                <AIPromptsManager
                  stage="resume_apply_fix"
                  promptTypes={['apply_fix']}
                  stageName="Адаптировать резюме"
                />
              )}

              {/* Request Parameters Sub-tab */}
              {aiSubTab === 'params' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Параметры запроса к AI</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Temperature */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Temperature: {aiSettings.temperature !== undefined ? aiSettings.temperature.toFixed(1) : '0.7'}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={aiSettings.temperature !== undefined ? aiSettings.temperature : 0.7}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          temperature: parseFloat(e.target.value)
                        })}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">Контролирует случайность ответов (0 = детерминированный, 2 = очень случайный)</p>
                    </div>

                    {/* Top P */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Top P: {aiSettings.topP !== undefined ? aiSettings.topP.toFixed(2) : '1.00'}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={aiSettings.topP !== undefined ? aiSettings.topP : 1.0}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          topP: parseFloat(e.target.value)
                        })}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">Nucleus sampling - вероятность выбора токенов (0-1)</p>
                    </div>

                    {/* Max Completion Tokens */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Completion Tokens
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={aiSettings.maxCompletionTokens || 2000}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          maxCompletionTokens: parseInt(e.target.value) || 2000
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Максимальное количество токенов для генерации ответа</p>
                    </div>

                    {/* Response Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Response Format
                      </label>
                      <select
                        value={aiSettings.responseFormat || 'text'}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          responseFormat: e.target.value as 'text' | 'json_object' | 'json_schema'
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="text">Text</option>
                        <option value="json_object">JSON Object</option>
                        <option value="json_schema">JSON Schema</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Формат ответа от AI модели</p>
                    </div>

                    {/* Presence Penalty */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Presence Penalty: {aiSettings.presencePenalty !== undefined ? aiSettings.presencePenalty.toFixed(1) : '0.0'}
                      </label>
                      <input
                        type="range"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={aiSettings.presencePenalty !== undefined ? aiSettings.presencePenalty : 0.0}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          presencePenalty: parseFloat(e.target.value)
                        })}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">Штраф за присутствие новых тем (-2 до 2)</p>
                    </div>

                    {/* Frequency Penalty */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frequency Penalty: {aiSettings.frequencyPenalty !== undefined ? aiSettings.frequencyPenalty.toFixed(1) : '0.0'}
                      </label>
                      <input
                        type="range"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={aiSettings.frequencyPenalty !== undefined ? aiSettings.frequencyPenalty : 0.0}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          frequencyPenalty: parseFloat(e.target.value)
                        })}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">Штраф за повторение токенов (-2 до 2)</p>
                    </div>

                    {/* Logprobs */}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={aiSettings.logprobs || false}
                          onChange={(e) => setAISettings({
                            ...aiSettings,
                            logprobs: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Logprobs</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Возвращать ли log probabilities для токенов</p>
                    </div>

                    {/* Top Logprobs */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Top Logprobs
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={aiSettings.topLogprobs || 0}
                        onChange={(e) => setAISettings({
                          ...aiSettings,
                          topLogprobs: Math.min(5, Math.max(0, parseInt(e.target.value) || 0))
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Количество top logprobs для возврата (0-5)</p>
                    </div>
                  </div>

                  {/* Stop Sequences */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stop Sequences (JSON массив)
                    </label>
                    <textarea
                      value={JSON.stringify(aiSettings.stopSequences || [], null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          if (Array.isArray(parsed)) {
                            setAISettings({
                              ...aiSettings,
                              stopSequences: parsed
                            });
                          }
                        } catch (err) {
                          // Игнорируем ошибки парсинга во время ввода
                        }
                      }}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder='["\\n", "END"]'
                    />
                    <p className="text-xs text-gray-500 mt-1">Массив строк, при встрече которых генерация останавливается (формат JSON)</p>
                  </div>
                </div>
              </div>
              )}

              {/* Save Button for provider, consultant, and params tabs */}
              {(aiSubTab === 'provider' || aiSubTab === 'consultant' || aiSubTab === 'params') && (
                <div className="flex justify-end">
                  <button
                    onClick={saveAISettings}
                    disabled={aiSaving}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Save className="h-5 w-5" />
                    <span>{aiSaving ? 'Сохранение...' : 'Сохранить AI настройки'}</span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-600">Не удалось загрузить AI настройки</p>
            </div>
          )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default SettingsPage;

